// ============================================================
// ToastHost.jsx — the platform's imperative toast surface.
//
// Mounted ONCE at the app root (App.js), exactly like LiveAnnouncer,
// and driven by a plain module function:
//
//     import { showError, showSuccess } from '../components/ToastHost';
//     catch (err) { showError(err.message || 'Save failed'); }
//
// WHY IMPERATIVE, WHEN <Toast> IS PARENT-CONTROLLED
// ------------------------------------------------------------------
// Toast's own contract (parent holds the state, parent renders one
// <Toast>) is unchanged and every existing consumer keeps working.
// This host exists for the case that contract handles badly: a message
// raised from a component that is NOT the page — a table ROW, a leaf
// dialog, a small form buried three levels down. There are 19 such
// components in the alert()-to-Toast sweep alone, several of them
// rendering a <tr>. Giving each its own state + <Toast> means 19 copies
// of the same three lines, and that boilerplate has ALREADY drifted in
// this codebase: five CareRota pages pass `type={toast.type}` to a
// component whose prop is `variant`, so every CareRota error toast
// renders green, polite and 3s instead of red, assertive and 5s.
// Nothing failed — the wrong prop is ignored and the default wins.
//
// LiveAnnouncer set this precedent deliberately and wrote down the
// reason: announce() "is a plain module function, not a hook or context
// value, so it is callable from portals, from effects, and from
// non-component code without prop-drilling a provider through every
// panel on the platform." The same argument applies here, so the same
// shape is used rather than a second, differently-shaped mechanism.
//
// Callers name an INTENT (showError / showSuccess) and never touch a
// prop name, so the drift above cannot recur through this path.
//
// SINGLE INSTANCE: this host shows one toast at a time, replacing any
// message already on screen — Toast has always been single-instance.
// A page that renders its own <Toast> is unaffected and unrelated.
//
// A message raised before mount is queued and flushed on mount, so an
// error thrown during the very first render pass is never dropped.
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import Toast from './Toast';

let emit = null;
const preMountQueue = [];

function push(message, variant) {
  if (!message || typeof message !== 'string') return;
  const trimmed = message.trim();
  if (!trimmed) return;
  if (emit) emit(trimmed, variant);
  else preMountQueue.push([trimmed, variant]);
}

/** Report a failure. Renders red, role="alert" (assertive), 5s. */
export function showError(message) { push(message, 'error'); }

/** Confirm a success. Renders green, role="status" (polite), 3s. */
export function showSuccess(message) { push(message, 'success'); }

/** Test seam: drop any queued-but-unflushed messages between tests. */
export function __resetToastHostForTests() {
  emit = null;
  preMountQueue.length = 0;
}

export default function ToastHost() {
  // `seq` increments on every push so two IDENTICAL consecutive messages
  // still produce a state change — a user retrying a failing save must be
  // told each time. (Same reason LiveAnnouncer alternates between two
  // child nodes; here a changed key is enough, because Toast remounts and
  // re-runs its own announce() + dismiss timer.)
  const [current, setCurrent] = useState(null);

  const receive = useCallback((message, variant) => {
    setCurrent((prev) => ({ message, variant, seq: (prev ? prev.seq : 0) + 1 }));
  }, []);

  useEffect(() => {
    emit = receive;
    while (preMountQueue.length) {
      const [message, variant] = preMountQueue.shift();
      receive(message, variant);
    }
    return () => { emit = null; };
  }, [receive]);

  const dismiss = useCallback(() => setCurrent(null), []);

  if (!current) return null;
  return (
    <Toast
      key={current.seq}
      message={current.message}
      variant={current.variant}
      onDismiss={dismiss}
    />
  );
}
